import { map } from 'rxjs/operators';
import { Injectable, Inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { SessionService } from '../session/session.service'
import { Arbetstagare } from '../interfaces/arbetstagare.interface';
import { InstanceSelector } from '../interfaces/instanceSelector.interface';
import { OtpKontaktinfo } from '../interfaces/otpKontaktinfo.interface';
import { detect } from 'detect-browser';
import { PortalMode } from '../enum/portalmode.enum'

@Injectable()
export class AuthService {
  constructor(
    protected http: HttpClient,
    protected router: Router,
    protected sessionService: SessionService,
    @Inject('API_URL') protected apiUrl: string) {
    const browserDetector = detect();

    this.isLoggedInSubject = new BehaviorSubject<number>(0);
    this.http.get<boolean>(`${this.apiUrl}isLoggedIn`)
      .subscribe((isLoggedIn) => {
        this.isLoggedInSubject.next(isLoggedIn ? 1 : 0);
        if (isLoggedIn) {

          const portalMode = this.sessionService.getPortalMode();

          if (portalMode === PortalMode.Norge) {
            return;
          }
          if (portalMode !== PortalMode.Borgenar && this.router.url.startsWith('/borgenar')) {
            this.router.navigate(['/select-instance']);
            return;
          }
          if (portalMode === PortalMode.Borgenar) {
            if (!this.router.url.startsWith('/borgenar')) {
              this.router.navigate(['/borgenar/start']);
            }
            return;
          }
          this.sessionService.getInstances().subscribe(
            (instanceSelector: InstanceSelector) => {
              if (this.router.routerState.snapshot.root.queryParams.code && this.router.routerState.snapshot.url.toString().includes('/login')) {
                return;
              }
              if (instanceSelector.instances.length === 1) {
                this.sessionService.setInstance(instanceSelector.instances[0]);
              }
              else if (!this.router.url.includes('mina-meddelanden')) {
                this.router.navigate(['/select-instance']);
              }
            },
            () => {
              if (location.pathname.includes('borgenar'))
                this.router.navigate(['/borgenar']);
              else
                this.router.navigate(['/home']);
            });
        }
      });
  }

  private GetBrowser(): string {
    if (navigator.userAgent.match(/OPT/))
      return "opera";

    const browserDetector = detect();
    return browserDetector.name;
  }

  private GetOs(): string {
    const browserDetector = detect();
    return browserDetector.os;
  }

  private isLoggedInSubject: BehaviorSubject<number>;
  public heartbeatId: number = 0;

  public logout(loggedOutByChoice?: boolean): void {
    this.stopHeartbeat();
    this.isLoggedInSubject.next(loggedOutByChoice ? 0 : 2);
  }

  public logoutFromCodeLink(): void {
    this.stopHeartbeat();
    this.isLoggedInSubject.next(3);
  }

  public setToken(token: string): void {
    this.sessionService.setToken(token);
  }

  public authMobileBankId(): Observable<IAuthResponse> {
    return this.http.get<IAuthResponse>(`${this.apiUrl}bankId/authMobile?browser=${this.GetBrowser()}&os=${this.GetOs()}&currentUrl=${window.location.href}`);
  }

  public authBankId(): Observable<IAuthResponse> {
    return this.http.get<IAuthResponse>(`${this.apiUrl}bankId/auth`);
  }

  public authAdminMobileBankId(personalNumber: string): Observable<IAuthResponse> {
    return this.http.get<IAuthResponse>(`${this.apiUrl}bankId/authAdminMobile?personalNumber=${personalNumber}&browser=${this.GetBrowser()}&os=${this.GetOs()}&currentUrl=${window.location.href}`);
  }

  public authAdminBankId(personalNumber: string): Observable<IAuthResponse> {
    return this.http.get<IAuthResponse>(`${this.apiUrl}bankId/authAdmin?personalNumber=${personalNumber}&browser=${this.GetBrowser()}&os=${this.GetOs()}&currentUrl=${window.location.href}`);
  }

  public authBankIdCreditor(): Observable<IAuthResponse> {
    return this.http.get<IAuthResponse>(`${this.apiUrl}bankId/authCreditor`);
  }

  public authMobileBankIdCreditor(): Observable<IAuthResponse> {
    return this.http.get<IAuthResponse>(
      `${this.apiUrl}bankId/authMobileCreditor?browser=${this.GetBrowser()}&os=${this.GetOs()}&currentUrl=${window.location.href}`
    );
  }

  public signBankId(instance: string, files: string[], trad: string): Observable<IAuthResponse> {
    return this.http.post<IAuthResponse>(`${this.apiUrl}bankId/signFiles`, { instanceId: instance, filiDbIds: files, tradId: trad, browser: this.GetBrowser(), os: this.GetOs(), currentUrl: window.location.href });
  }

  public collectBankId(orderRef: string, registrationCode: string = null): Observable<ICollectResponse> {
    const codeUrlPart = registrationCode ? `?registrationCode=${registrationCode}` : '';
    return this.http.get<ICollectResponse>(`${this.apiUrl}bankId/collect/${orderRef}${codeUrlPart}`);
  }

  public getQrCode(autoStartToken: string): Observable<string> {
    return this.http.get<string>(`${this.apiUrl}bankId/qr/${autoStartToken}`);
  }

  public cancelBankId(orderRef: string) {
    return this.http.get(`${this.apiUrl}bankId/cancel/${orderRef}`);
  }

  public getOtpContactInfo(personalNumber: string): Observable<OtpKontaktinfo> {
    return this.http.get<OtpKontaktinfo>(`${this.apiUrl}otp/contactInfo/${personalNumber}`);
  }

  public sendOtp(personalNumber: string, id: string, lang: string): Observable<IOtpRequestData> {
    return this.http.post<IOtpRequestData>(`${this.apiUrl}otp/send`, { personnummer: personalNumber, kontaktinfo: id, lang: lang });
  }

  public authOtp(requestId: string, otp: string): Observable<IOtpCompletionData> {
    return this.http.post<IOtpCompletionData>(`${this.apiUrl}otp/auth`, { requestId: requestId, otp: otp });
  }

  public initSignOtp(instance: string, files: string[], lang: string): Observable<IOtpRequestData> {
    return this.http.post<IOtpRequestData>(`${this.apiUrl}otp/initSignFiles`, { instanceId: instance, filiDbIds: files, lang: lang, browser: this.GetBrowser(), os: this.GetOs(), currentUrl: window.location.href });
  }

  public signOtp(instance: string, request: string, otp: string): Observable<number> {
    return this.http.post<number>(`${this.apiUrl}otp/signFiles`, { instanceId: instance, requestId: request, otp: otp });
  }

  public getUserInfo(instance: string): Observable<Arbetstagare> {
    return this.http.get<Arbetstagare>(`${this.apiUrl}${instance}/arbetstagare/get`);
  }

  public updateUserInfo(instance: string, arbetstagare: Arbetstagare): Observable<Arbetstagare> {
    return this.http.post<Arbetstagare>(`${this.apiUrl}${instance}/arbetstagare/update`, arbetstagare);
  }

  public startHeartbeat(): void {
    if (this.heartbeatId) {
      return;
    }

    const heartrate: number = 5 * 60 * 1000; // fem minuter
    console.info(`Startar hjärtslag! (intervall = ${heartrate / 1000} s)`);
    this.heartbeatId = window.setInterval(() => {
      this.http.get<boolean>(`${this.apiUrl}Heartbeat`)
        .toPromise()
        .then((isLoggedIn: boolean) => {
          console.info(`Hjärtslag! (ID = ${this.heartbeatId})`);
          if (!isLoggedIn) {
            this.logout();
          }
        })
        .catch(error => {
          console.error(error);
        });
    }, heartrate);
  }

  public stopHeartbeat(): void {
    if (!this.heartbeatId) {
      return;
    }

    console.info(`Stoppar hjärtslag! (ID = ${this.heartbeatId})`);
    clearInterval(this.heartbeatId);
    this.heartbeatId = 0;
  }

  public isLoggedIn(): Observable<boolean> {
    return this.http.get<boolean>(`${this.apiUrl}isLoggedIn`).pipe(map(
      (isLoggedIn: boolean) => {
        if (!isLoggedIn) {
          return false;
        }

        this.startHeartbeat();
        this.isLoggedInSubject.next(1);
        return true;
      },
      (error) => {
        console.error(error);
      }
    ));
  }

  public getCurrentUser(instanceId: string): Observable<string> {
    return this.http.get<string>(`${this.apiUrl}currentUser/${instanceId}`);
  }

  public getNumberOfInstances(): Observable<number> {
    return this.http.get<number>(`${this.apiUrl}numberOfInstances`);
  }

  public getIsLoggedInMessage(): Observable<number> {
    return this.isLoggedInSubject.asObservable();
  }
}

export interface IAuthResponse {
  orderRef: string;
  autoStartToken: string;
  qrCode?: string;
  status: string;
  completionData?: any;
}

export interface ICollectResponse {
  orderRef: string;
  status: string;
  failed: string;
  complete: string;
  hintCode: string
  qrCode?: string;
  completionData: ICompletionData;
  timedOut: boolean;
}

export interface ICompletionData {
  user: ICompletionDataUser;
  device: ICompletionDataDevice;
  signature: string;
  accessToken: string;
}

export interface ICompletionDataUser {
  personalNumber: string;
  name: string;
  givenName: string;
  surname: string;
}

export interface ICompletionDataDevice {
  ipAddress: string;
}

export interface IOtpCompletionData {
  accessToken: string;
  ipAddress: string;
  personalNumber: string;
}

export interface IOtpRequestData {
  requestId: string;
  recipient?: string;
  filesToSign?: string[];
}
